import {
  getCurrentUserId,
  getUserMetadata,
  getCurrentUser,
} from "../selectors/user";
import {
  formatRouteDetailData,
  getCurrentRouteId,
  getRouteDetail,
  getRouteStops,
  isCurrentUserLock,
  isRouteReadyOrOpen,
} from "../selectors/route";
import * as RouteService from "../../services/routes";
import { hideNotifications, showNotification } from "./notifications";
import { NotificationType } from "../../features/Notifier";
import { getSelectedDepot } from "../selectors/app";
import { moveStops } from "../../models/moveStops";
import {
  DISMISS,
  LOCKED_BY,
  UNABLE_TO_UNLOCK_ROUTE,
  UNLOCK,
} from "../../constants/strings";
import { clearAll } from "./ui/routeDetail";
import {
  getPreparedStops,
  getSelectedPreparedStops,
} from "../selectors/ui/routeDetail";
import {
  hasInvalidStopNumbers,
  prepareNoStopNumberRouteStops,
  prepareRoute,
  updateStopsWithValidStopNumber,
} from "../../models/route";
import { endLoadingProcess, startLoadingProcess } from "./loader";
import {
  changeRouteOpportunityTime,
  updateListRoute,
  updateRouteInList,
} from "./routes";
import { RouteStatus } from "../../enums/RouteFilterTypes";
import { getRoutesList } from "../selectors/routes";
import { getErrorMessage } from "../../utils/errors";
import {
  LOAD_ROUTE_DETAILS_PROCESS,
  LOAD_SAVE_PROCESS,
} from "../../enums/loaderProcess";
import { resetAllRequestsExceptDeferred } from "./request";
import { resetStopDataChanged } from "../../features/routeDetail/model";

export const unlockSnackbarKey = "unlockSnackbarKey";

const SET_CURRENT_USER_LOCK = "SET_CURRENT_USER_LOCK";
const FETCH_ROUTE_DETAIL_COMPLETE = "FETCH_ROUTE_DETAIL_COMPLETE";
const CLEAR_ROUTE_DETAIL = "CLEAR_ROUTE_DETAIL";
const SET_STOPS = "SET_STOPS";
const SET_SCHEDULE_REQUIRED = "SET_SCHEDULE_REQUIRED";
const UPDATE_STOP = "UPDATE_STOP";
const SET_ROUTE_DETAIL = "SET_ROUTE_DETAIL";
const SET_REQUESTS = "SET_REQUESTS";
const SET_TRANSFER_ROUTE_ID = "SET_TRANSFER_ROUTE_ID";
const SET_TRANSFER_FROM_ROUTE_ID = "SET_TRANSFER_FROM_ROUTE_ID";
const ADD_STOP = "ADD_STOP";
const SET_ROUTE_CHECKSUM = "SET_ROUTE_CHECKSUM";
const REQUEST_SUBMITTING_STATUS = "REQUEST_SUBMITTING_STATUS";
const SET_SAVE_ROUTE_ENABLE = "SET_SAVE_ROUTE_ENABLE";
const UPDATE_ROUTE_STATUS = "UPDATE_ROUTE_STATUS";
const SET_ORIGINAL_STOPS_DATA = "SET_ORIGINAL_STOPS_DATA";
const SET_INITIAL_DOWNGRADE_ATTEMPTS = "SET_INITIAL_DOWNGRADE_ATTEMPTS";
const INCREMENT_REQUEST_ORDER = "INCREMENT_REQUEST_ORDER";
const RESET_REQUEST_ORDER = "RESET_REQUEST_ORDER";
const SET_TRANSFERRED_STOP = "SET_TRANSFERRED_STOP";

export const actionTypes = {
  SET_CURRENT_USER_LOCK,
  FETCH_ROUTE_DETAIL_COMPLETE,
  SET_STOPS,
  SET_SCHEDULE_REQUIRED,
  UPDATE_STOP,
  SET_ROUTE_DETAIL,
  CLEAR_ROUTE_DETAIL,
  SET_REQUESTS,
  SET_TRANSFER_ROUTE_ID,
  SET_TRANSFER_FROM_ROUTE_ID,
  ADD_STOP,
  SET_ROUTE_CHECKSUM,
  REQUEST_SUBMITTING_STATUS,
  SET_SAVE_ROUTE_ENABLE,
  UPDATE_ROUTE_STATUS,
  SET_ORIGINAL_STOPS_DATA,
  SET_INITIAL_DOWNGRADE_ATTEMPTS,
  SET_TRANSFERRED_STOP,
  INCREMENT_REQUEST_ORDER,
  RESET_REQUEST_ORDER,
};

export const setCurrentUserLock = (currentUserLock) => ({
  type: SET_CURRENT_USER_LOCK,
  payload: currentUserLock,
});

export const setInitialDowngradeAttempts = (attempt) => ({
  type: SET_INITIAL_DOWNGRADE_ATTEMPTS,
  payload: attempt,
});

export const setTransferredStops = (payload) => ({
  type: SET_TRANSFERRED_STOP,
  payload,
});

export const updateRouteStatus = (status) => ({
  type: UPDATE_ROUTE_STATUS,
  payload: status,
});

export const clearRouteDetail = () => ({
  type: CLEAR_ROUTE_DETAIL,
});

export const setOriginalStopsData = (stopsData = []) => ({
  type: SET_ORIGINAL_STOPS_DATA,
  payload: stopsData,
});

export const lockRoute = (defaultRoutDetail) => async (dispatch, getState) => {
  let message;
  const state = getState();
  const currentUserLock = isCurrentUserLock(state);
  const { username } = getUserMetadata(state);
  const routeDetail = defaultRoutDetail || getRouteDetail(state);
  const depotCode = getSelectedDepot(state);
  const route = getRoutesList(state)[routeDetail.routeId];

  if (currentUserLock) {
    return true;
  }

  try {
    const response = await RouteService.lockRoute(
      depotCode,
      username,
      routeDetail,
      routeDetail?.originalStopsData
    );

    const { checksum, lockFailReason, routeStatus } = response.data.route[0];

    if (lockFailReason) {
      message = lockFailReason;
      return false;
    }

    if (routeDetail?.originalStopsData) {
      dispatch(setOriginalStopsData());
    }

    if (!defaultRoutDetail) {
      dispatch(setRouteChecksum(checksum));
      dispatch(setCurrentUserLock(true));
      dispatch(updateRouteStatus(routeStatus));
      dispatch(showLockedByNotification(username));
    }

    dispatch(
      updateListRoute({
        ...route,
        checksum,
        routeLockedBy: username,
      })
    );

    return true;
  } catch (error) {
    message = "Unable to lock route, please try again";
    return false;
  } finally {
    message &&
      dispatch(
        showNotification({
          key: "lock-unlock-message",
          type: NotificationType.ACTION,
          message,
          buttonText: DISMISS,
        })
      );
  }
};

/**
 * @param {boolean} [forceUnlock]
 * @param {object} [defaultRouteDetail]
 */
export const unlockRoute = (forceUnlock = false, defaultRouteDetail) => async (
  dispatch,
  getState
) => {
  const state = getState();
  const routeDetail = defaultRouteDetail || getRouteDetail(state);
  const depotCode = getSelectedDepot(state);
  const { username } = getUserMetadata(state);
  const currentUserLock = isCurrentUserLock(state);
  const route = getRoutesList(state)[routeDetail.routeId];

  const isLocked = routeDetail.routeLockedBy || currentUserLock;
  if (!isLocked) {
    return routeDetail.checksum;
  }
  try {
    const {
      data: { route: routeResult },
    } = await RouteService.unlockRoute(
      depotCode,
      username,
      routeDetail,
      forceUnlock
    );

    const { checksum, unlockFailReason } = routeResult[0];

    if (unlockFailReason) {
      await Promise.reject(unlockFailReason);
    } else {
      dispatch(hideNotifications([unlockSnackbarKey]));
      dispatch(setCurrentUserLock(false));

      dispatch(
        updateListRoute({
          ...route,
          checksum,
          routeLockedBy: null,
        })
      );

      return checksum;
    }
  } catch (error) {
    dispatch(
      showNotification({
        key: "lock-unlock-message",
        type: NotificationType.ACTION,
        message: typeof error === "string" ? error : UNABLE_TO_UNLOCK_ROUTE,
        buttonText: DISMISS,
      })
    );
    return routeDetail.checksum;
  }
};

export const forceUnlockRoute = () => async (dispatch) => {
  const checksum = await dispatch(unlockRoute(true));
  checksum && dispatch(setRouteChecksum(checksum));
};

export const updateCurrentUserLockForRoute = (routeLockedBy) => (
  dispatch,
  getState
) => {
  const { username } = getUserMetadata(getState());
  const isLockedByCurrentUser = routeLockedBy === username;

  if (routeLockedBy && isLockedByCurrentUser) {
    dispatch(setCurrentUserLock(true));
  } else {
    dispatch(setCurrentUserLock(false));
  }

  dispatch(controlLockedByNotification(routeLockedBy));
};

export const showLockedByNotification = (lockedBy) => (dispatch, getState) => {
  const state = getState();
  const currentUser = getCurrentUser(state);
  dispatch(hideAllNotifications());
  dispatch(
    showNotification({
      key: unlockSnackbarKey,
      type:
        currentUser.metadata.routePlannerUnlock &&
        !isCurrentUserLock(state) &&
        NotificationType.ACTION,
      message: `${LOCKED_BY} ${lockedBy}`,
      buttonText: UNLOCK,
      onConfirm: async () => {
        await dispatch(forceUnlockRoute());
      },
    })
  );
};

export const hideAllNotifications = () => (dispatch) => {
  dispatch(hideNotifications([unlockSnackbarKey, "lock-unlock-message"]));
};

export const fetchRouteDetail = (depotCode, routeId) => async (dispatch) => {
  dispatch(startLoadingProcess(LOAD_ROUTE_DETAILS_PROCESS));
  dispatch(clearAll());

  try {
    let payload = await RouteService.getRouteDetail(depotCode, routeId);
    const routeProcessedInSaturn =
      payload.status === RouteStatus.Loading ||
      payload.status === RouteStatus.Downloaded;

    if (!routeProcessedInSaturn) {
      if (!payload.stopNumbersSet) {
        const {
          originalStopsData,
          updatedStops,
        } = prepareNoStopNumberRouteStops(payload.stop);
        payload = { ...payload, stop: updatedStops, originalStopsData };
      } else if (hasInvalidStopNumbers(payload?.stop)) {
        const {
          originalStopsData,
          updatedStops,
        } = updateStopsWithValidStopNumber(payload.stop);
        payload = { ...payload, stop: updatedStops, originalStopsData };
      }
    }
    dispatch(completeRouteFetch(payload));
    dispatch(updateCurrentUserLockForRoute(payload?.routeLockedBy));
    dispatch(setScheduleRequired(payload?.scheduleRequired));
  } catch {
    dispatch(completeRouteFetch());
  } finally {
    dispatch(endLoadingProcess(LOAD_ROUTE_DETAILS_PROCESS));
  }
};

export const completeRouteFetch = (payload = null) => ({
  type: FETCH_ROUTE_DETAIL_COMPLETE,
  payload,
});

export const updateStop = (stop) => ({
  type: UPDATE_STOP,
  payload: stop,
});

export const addStop = (stop) => ({
  type: ADD_STOP,
  payload: stop,
});

export const setScheduleRequired = (payload = false) => ({
  type: SET_SCHEDULE_REQUIRED,
  payload,
});

export const setSaveRouteEnable = (payload = true) => ({
  type: SET_SAVE_ROUTE_ENABLE,
  payload,
});

export const setStops = (payload) => ({
  type: SET_STOPS,
  payload,
});

export const moveSelectedStops = ({ direction, separatorStopNumber }) => async (
  dispatch,
  getState
) => {
  const currentUserLock = await dispatch(lockRoute());

  if (!currentUserLock) {
    return;
  }

  const state = getState();
  const { status } = getRouteDetail(state);
  const stop = getPreparedStops(state);
  const selectedStops = getSelectedPreparedStops(state);

  const updatedStops = moveStops({
    stop,
    selectedStops,
    status,
    direction,
    separatorStopNumber,
  });

  dispatch(setStops(updatedStops));
  dispatch(setScheduleRequired(true));
  dispatch(setSaveRouteEnable(isRouteReadyOrOpen(state)));
};

export const setRouteDetail = (payload) => ({
  type: SET_ROUTE_DETAIL,
  payload,
});

export const setRouteChecksum = (payload) => ({
  type: SET_ROUTE_CHECKSUM,
  payload,
});

export const setRequests = (payload) => ({
  type: SET_REQUESTS,
  payload,
});

export const setRequestStatus = (payload) => ({
  type: REQUEST_SUBMITTING_STATUS,
  payload,
});

export const resetStopData = () => (dispatch, getState) => {
  const stops = getRouteStops(getState());
  const updatedStops = resetStopDataChanged(stops);
  dispatch(setStops(updatedStops));
};

const resetDataAfterSave = () => (dispatch) => {
  dispatch(resetAllRequestsExceptDeferred());
  dispatch(resetStopData());
  dispatch(clearAll());
  dispatch(setInitialDowngradeAttempts(null));
  dispatch(resetRequestOrder());
};

export const saveRoute = () => async (dispatch, getState) => {
  const state = getState();
  const depotCode = getSelectedDepot(state);
  const routeId = getCurrentRouteId(state);
  const routeData = formatRouteDetailData(state);
  try {
    dispatch(startLoadingProcess(LOAD_SAVE_PROCESS));
    const { checksum } = await RouteService.saveRoute(
      depotCode,
      routeId,
      routeData
    );
    checksum && dispatch(setRouteChecksum(checksum));
    await dispatch(
      updateRouteInList(routeId, {
        scheduleRequired: routeData.route.scheduleRequired,
      })
    );

    dispatch(resetDataAfterSave());

    const unlockChecksum = await dispatch(unlockRoute(false));
    unlockChecksum && dispatch(setRouteChecksum(unlockChecksum));
    dispatch(
      changeRouteOpportunityTime(
        routeData.route.totalRouteOpportunityTime,
        routeId
      )
    );
    return true;
  } catch (error) {
    dispatch(
      showNotification({
        type: NotificationType.ACTION,
        message: getErrorMessage(error),
        buttonText: DISMISS,
      })
    );

    return false;
  } finally {
    dispatch(endLoadingProcess(LOAD_SAVE_PROCESS));
    dispatch(setSaveRouteEnable(false));
  }
};

export const setTransferRouteId = (routeId = null) => ({
  type: SET_TRANSFER_ROUTE_ID,
  payload: routeId,
});

export const setTransferFromRouteId = (routeId = null) => ({
  type: SET_TRANSFER_FROM_ROUTE_ID,
  payload: routeId,
});

export const incrementRequestOrder = () => ({
  type: INCREMENT_REQUEST_ORDER,
});

export const resetRequestOrder = () => ({
  type: RESET_REQUEST_ORDER,
});

export const resetAllTransferFromRouteEntity = () => (dispatch) => {
  dispatch(setTransferRouteId());
  dispatch(setTransferFromRouteId());
};

export const createRoute = (formValues, multiRunRoute) => async (
  dispatch,
  getState
) => {
  const state = getState();
  const depot = getSelectedDepot(state);
  const uid = getCurrentUserId(state);
  const route = prepareRoute(formValues, uid, multiRunRoute);

  try {
    await RouteService.createRoute(depot, route);
    return true;
  } catch (error) {
    dispatch(
      showNotification({
        type: NotificationType.ACTION,
        message: getErrorMessage(error),
        buttonText: DISMISS,
        key: "new-route-time-overlaps-another-route",
      })
    );

    return false;
  }
};

export const controlLockedByNotification = (routeLockedBy) => (dispatch) => {
  if (routeLockedBy) {
    dispatch(showLockedByNotification(routeLockedBy));
  } else {
    dispatch(hideNotifications([unlockSnackbarKey]));
  }
};

export const manuallyLockChange = (routeDetail) => async (
  dispatch,
  getState
) => {
  const { routeLockedBy } = routeDetail;
  const { username, routePlannerUnlock } = getUserMetadata(getState());

  if (routeLockedBy && (routeLockedBy === username || routePlannerUnlock)) {
    await dispatch(unlockRoute(routePlannerUnlock, routeDetail));
  } else {
    await dispatch(lockRoute(routeDetail));
  }
};
